export class CapacitorNotificationProvider {
    LocalNotifications = null;
    isInitialized = false;
    analytics;
    constructor(analytics) {
        this.analytics = analytics;
        this.initializeCapacitor();
    }
    async initializeCapacitor() {
        try {
            // Check if we're in a Capacitor environment first
            if (typeof window !== 'undefined' && 'Capacitor' in window) {
                const Capacitor = window.Capacitor;
                if (!Capacitor.isNativePlatform()) {
                    console.log('Not running on Capacitor platform, skipping LocalNotifications initialization');
                    return;
                }
            }
            else {
                console.log('Capacitor not available, skipping LocalNotifications initialization');
                return;
            }
            const { LocalNotifications } = await import('@capacitor/local-notifications');
            this.LocalNotifications = LocalNotifications;
            // Request permissions
            await this.requestPermissions();
            this.isInitialized = true;
            console.log('Capacitor Local Notifications initialized successfully');
        }
        catch (error) {
            console.log('Capacitor Local Notifications not available (likely web environment):', error instanceof Error ? error.message : 'Unknown error');
        }
    }
    async requestPermissions() {
        if (!this.LocalNotifications)
            return false;
        try {
            const permission = await this.LocalNotifications.requestPermissions();
            return permission.display === 'granted';
        }
        catch (error) {
            console.error('Failed to request notification permissions:', error);
            return false;
        }
    }
    async scheduleNotification(data, delay = 0) {
        if (!this.isInitialized || !this.LocalNotifications) {
            console.warn('Capacitor Local Notifications not initialized');
            return;
        }
        try {
            const scheduleTime = new Date();
            scheduleTime.setSeconds(scheduleTime.getSeconds() + delay);
            const notification = {
                title: data.title,
                body: data.body,
                id: Math.floor(Math.random() * 1000000),
                schedule: delay > 0 ? { at: scheduleTime } : undefined,
                sound: 'default',
                android: {
                    channelId: 'default',
                    smallIcon: data.icon || 'ic_stat_icon_default',
                    color: '#FF0000',
                },
                ios: {
                    badge: data.badge ? parseInt(data.badge) : undefined,
                    sound: 'default.wav',
                },
                extra: data.data || {},
            };
            if (delay === 0) {
                // Send immediately
                await this.LocalNotifications.schedule({
                    notifications: [notification],
                });
            }
            else {
                // Schedule for later
                await this.LocalNotifications.schedule({
                    notifications: [notification],
                });
            }
            console.log('Capacitor notification scheduled:', notification);
            // Track notification scheduled analytics
            if (this.analytics && delay > 0) {
                const notificationType = data.data?.type || 'unknown';
                const characterId = data.data?.characterId;
                await this.analytics.trackEvent('local_notification_scheduled', {
                    notification_type: notificationType,
                    character_id: characterId || 'system',
                    delay_seconds: delay,
                    platform: 'capacitor',
                });
            }
        }
        catch (error) {
            console.error('Failed to schedule Capacitor notification:', error);
        }
    }
    async cancelAll() {
        if (!this.isInitialized || !this.LocalNotifications)
            return;
        try {
            await this.LocalNotifications.cancelAll();
            console.log('All Capacitor notifications cancelled');
        }
        catch (error) {
            console.error('Failed to cancel Capacitor notifications:', error);
        }
    }
    async getPending() {
        if (!this.isInitialized || !this.LocalNotifications)
            return [];
        try {
            const result = await this.LocalNotifications.getPending();
            return result.notifications || [];
        }
        catch (error) {
            console.error('Failed to get pending Capacitor notifications:', error);
            return [];
        }
    }
    async registerActionTypes() {
        if (!this.isInitialized || !this.LocalNotifications)
            return;
        try {
            await this.LocalNotifications.registerActionTypes({
                types: [
                    {
                        id: 'OPEN_APP',
                        actions: [
                            {
                                id: 'open',
                                title: 'Open App',
                                destructive: false,
                            },
                            {
                                id: 'dismiss',
                                title: 'Dismiss',
                                destructive: true,
                            },
                        ],
                    },
                ],
            });
        }
        catch (error) {
            console.error('Failed to register action types:', error);
        }
    }
    setupNotificationListeners() {
        if (!this.isInitialized || !this.LocalNotifications)
            return;
        // Listen for notification received
        this.LocalNotifications.addListener('localNotificationReceived', async (notification) => {
            console.log('Notification received:', notification);
            // Track notification received analytics
            if (this.analytics) {
                const notificationType = notification.extra?.type || 'unknown';
                const characterId = notification.extra?.characterId;
                await this.analytics.trackEvent('local_notification_received', {
                    notification_type: notificationType,
                    character_id: characterId || 'system',
                    platform: 'capacitor',
                });
            }
            // Dispatch custom event for the app to handle
            const event = new CustomEvent('capacitor-notification-received', {
                detail: notification,
            });
            window.dispatchEvent(event);
        });
        // Listen for notification action performed
        this.LocalNotifications.addListener('localNotificationActionPerformed', async (notification) => {
            console.log('Notification action performed:', notification);
            // Track notification open analytics (user clicked/opened)
            if (this.analytics) {
                const notificationType = notification.notification?.extra?.type || 'unknown';
                const characterId = notification.notification?.extra?.characterId;
                await this.analytics.trackEvent('local_notification_open', {
                    notification_type: notificationType,
                    character_id: characterId || 'system',
                    platform: 'capacitor',
                });
            }
            // Dispatch custom event for the app to handle
            const event = new CustomEvent('capacitor-notification-action', {
                detail: notification,
            });
            window.dispatchEvent(event);
        });
    }
    isAvailable() {
        return this.isInitialized && this.LocalNotifications !== null;
    }
}
